import { useState, useMemo, useCallback, useEffect } from 'react';

// Utils
import { getStandingsCompetitionDetails } from '../../services/standings/standings';
import useFetchIntervalData from '../useFetchIntervalData';
import { normalizeStandingsCompetitionResponseData } from '@sportal365/smp-widget-normalizers/src/standings/normalizeStandingCompetitionResponseData';
import { mapToOptions } from '../../utils';

// Types
import { RefreshTimes, SDKOptions } from '../../types';
import { IStandingsCompetitionResponse, IStandingsConfig, IStandingsSeasonResponse } from '../../types/standings';

interface IUseStandingsCompetitionArgs {
  dataCompetition?: string;
  dataStage?: string;
  dataSeason?: string;
  sport: string;
  dataRefreshTime?: RefreshTimes;
  dataEntityLinks?: IStandingsConfig['dataEntityLinks'];
  sdkOptions: SDKOptions;
}

export const useStandingsCompetition = ({
  dataCompetition,
  dataStage,
  dataSeason,
  dataRefreshTime,
  dataEntityLinks,
  sport,
  sdkOptions,
}: IUseStandingsCompetitionArgs) => {
  const [isLoading, setIsLoading] = useState(false);
  const [season, setSeason] = useState(dataSeason);
  const [stage, setStage] = useState(dataStage);
  const [competition, setCompetition] = useState<IStandingsCompetitionResponse>();
  const [seasons, setSeasons] = useState<IStandingsSeasonResponse[]>();

  const params = {
    competition_id: dataCompetition,
    sport,
  };

  const fetchStandingsCompetition = useCallback(
    (manageLoader: boolean) => {
      if (dataCompetition) {
        manageLoader && setIsLoading(true);
        getStandingsCompetitionDetails({ params, sdkOptions })
          ?.then(({ available_seasons, competition }) => {
            manageLoader && setIsLoading(false);
            competition && setCompetition(competition);
            available_seasons && setSeasons(available_seasons);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    },
    [dataCompetition],
  );

  useEffect(() => {
    fetchStandingsCompetition(true);
  }, [fetchStandingsCompetition]);

  useFetchIntervalData(fetchStandingsCompetition, dataRefreshTime);

  const normalizedCompetition = normalizeStandingsCompetitionResponseData({ competition, dataEntityLinks });

  const stages = useMemo(() => {
    if (!season) {
      if (!stage) {
        const defaultSeason = seasons && seasons[0];
        setSeason(defaultSeason?.id);
        return defaultSeason?.stages ?? [];
      } else {
        const selectedSeason = seasons?.find((season) => season.stages?.some(({ id }) => id === stage));
        setSeason(selectedSeason?.id);
        return selectedSeason?.stages ?? [];
      }
    }

    return seasons?.find(({ id }) => id === season)?.stages?.filter((stage) => stage.available_playoffs || stage.available_standings) ?? [];
  }, [seasons, stage]);

  useEffect(() => {
    if (!stage) {
      const defaultStage = stages && stages[0]?.id;
      setStage(defaultStage);
    }
  }, [stage, stages]);

  const pillTabOptions = useMemo(() => mapToOptions(stages), [stages]);
  const seasonOptions = useMemo(() => mapToOptions(seasons), [seasons]);

  return { isLoading, competition: normalizedCompetition, pillTabOptions, seasonOptions, season, setSeason, stage, stages, setStage };
};
